import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { useVisitorInlineEdit } from "../utils/useVisitorInlineEdit";
import VisitorEditPanel from "../components/form/VisitorEditPanel";
import AdminDashboardCards from "../components/admin/AdminDashboardCards";
import PurposeMasterSection from "../components/masters/purpose/PurposeMasterSection";
import IdProofMasterSection from "../components/masters/idProof/IdProofMasterSection";
import VisitorDirectorySection from "../components/directory/VisitorDirectorySection";
import DeleteVisitorModal from "../components/modals/DeleteVisitorModal";
import ExitVisitorModal from "../components/modals/ExitVisitorModal";
import { VisitorProvider } from "../components/context/VisitorContext";
import { useVisitorRole } from "../utils/useVisitorRole";
import { VISITOR_CONTROL_SUBMODULE_SLUG } from "../utils/visitorPermissionSlugs";
import { useVisitorDashboardStats } from "../utils/useVisitorDashboardStats";
import { notifyVisitorDataChanged } from "../utils/visitorDataEvents";
import "../styles/visitorDashboard.css";
import "../styles/visitorTable.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function VisitorControlContent() {
  const location = useLocation();
  const legacyEditHandled = useRef(false);
  const perms = useVisitorRole(VISITOR_CONTROL_SUBMODULE_SLUG);
  const {
    editingVisitor,
    editError,
    openEdit,
    openEditById,
    cancelEdit,
    clearEdit,
  } = useVisitorInlineEdit("visitor-control-edit");
  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [exitTarget, setExitTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [mastersVersion] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const { dashboardStats, loadingStats, refreshStats } = useVisitorDashboardStats({
    enabled: perms.canView,
    reloadToken,
    listScope: "all",
  });

  useEffect(() => {
    const editId = location.state?.editVisitorId;
    if (!editId || legacyEditHandled.current) return;
    legacyEditHandled.current = true;
    openEditById(editId);
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state?.editVisitorId, location.pathname, openEditById]);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    clearEdit();
    setReloadToken((t) => t + 1);
    notifyVisitorDataChanged();
    refreshStats({ silent: true });
  };

  const handleEditSuccess = (msg) => {
    handleSuccess(msg);
    cancelEdit();
  };

  const quickJumpItems = useMemo(() => {
    const items = [];
    if (perms.canView) {
      items.push({
        key: "stats",
        label: "Performance overview",
        targetId: "visitor-control-stats",
      });
      items.push({
        key: "list",
        label: "Visitor directory",
        targetId: "visitor-control-list",
      });
    }
    if (editingVisitor && perms.canEdit) {
      items.push({ key: "edit", label: "Edit visitor", targetId: "visitor-control-edit" });
    }
    if (perms.canManageMasters) {
      items.push({
        key: "masters",
        label: "Master table management",
        targetId: "visitor-control-masters",
      });
    }
    return items;
  }, [
    editingVisitor,
    perms.canView,
    perms.canEdit,
    perms.canManageMasters,
  ]);

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative area</p>
          <h2 className="user-builder-hero__title">Visitors control</h2>
          <p className="user-builder-hero__sub">
            All visitor entries, directory filters, and master tables (purposes, ID proof types).
            Permissions follow visitor-control in User builder.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={editError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="visitor-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !perms.canView && !perms.canManageMasters ? (
        <GlobalFeedback
          type="warning"
          text="No permission for visitors control. Enable View on visitor-control in User builder, then sign in again."
          role="status"
        />
      ) : null}
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && perms.canView ? (
            <section
              id="visitor-control-stats"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Performance overview</h3>
                <p className="user-builder-panel__hint">Live statistics for all visitor entries.</p>
              </div>
              <AdminDashboardCards loading={loadingStats} dashboardStats={dashboardStats} />
            </section>
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
            <section
              id="visitor-control-list"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <VisitorDirectorySection
                isAdmin
                listScope="all"
                showPanelHead
                permissions={perms}
                roleSlug={perms.roleSlug}
                reloadToken={reloadToken}
                onEdit={perms.canEdit ? openEdit : undefined}
                onDelete={perms.canDelete ? setDeleteTarget : undefined}
                onExit={perms.canExit ? setExitTarget : undefined}
              />
            </section>
          ) : null}

          {editingVisitor ? (
            <section
              id="visitor-control-edit"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <VisitorEditPanel
                visitor={editingVisitor}
                onCancel={cancelEdit}
                onSuccess={handleEditSuccess}
              />
            </section>
          ) : null}

          {perms.canManageMasters ? (
            <section
              id="visitor-control-masters"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Master table management</h3>
                <p className="user-builder-panel__hint">
                  Visit purposes and ID proof types used on the register form.
                </p>
              </div>
              <div
                className="admin-masters-unified"
                style={{ display: "flex", flexDirection: "column", gap: "2rem", marginTop: "1rem" }}
              >
                <div id="visitor-purpose-master" style={{ scrollMarginTop: "2rem" }}>
                  <h4 className="visitor-list-panel__section-title">Visit purposes</h4>
                  <PurposeMasterSection key={`purpose-${mastersVersion}`} />
                </div>
                <div id="visitor-id-proof-master" style={{ scrollMarginTop: "2rem" }}>
                  <h4 className="visitor-list-panel__section-title">ID proof types</h4>
                  <IdProofMasterSection key={`idproof-${mastersVersion}`} />
                </div>
              </div>
            </section>
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <GlobalQuickJump
            asideAriaLabel="Visitors control navigation"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <ExitVisitorModal
        visitor={exitTarget}
        onClose={() => setExitTarget(null)}
        onSuccess={handleSuccess}
      />

      <DeleteVisitorModal
        visitor={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />
    </div>
  );
}

/** Admin — mirrors `call-logs/pages/CallLogsControlPage.jsx`. */
export default function VisitorControlPage() {
  return (
    <VisitorProvider>
      <VisitorControlContent />
    </VisitorProvider>
  );
}
